/**
 * Dialog for deleting files with dependency checking
 */
import { useState } from 'react';
import { Trash2, AlertTriangle, FileX } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { File } from '@phrasewright/types';

interface DeleteFileDialogProps {
  open: boolean;
  onClose: () => void;
  onDelete: () => Promise<void>;
  file: File;
  dependencies: File[];
}

export function DeleteFileDialog({
  open,
  onClose,
  onDelete,
  file,
  dependencies,
}: DeleteFileDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setIsLoading(true);
    setError('');

    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError('');
      onClose();
    }
  };

  const hasDependencies = dependencies.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" />
            Delete File
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. The file will be permanently deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File info */}
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <FileX className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <div className="font-medium text-sm">{file.name}</div>
              <div className="text-xs text-muted-foreground">{file.path}</div>
            </div>
            <Badge variant="secondary">{file.metadata.type}</Badge>
          </div>

          {/* Dependencies warning */}
          {hasDependencies && (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <div className="flex items-center gap-2 text-destructive mb-2">
                <AlertTriangle className="h-4 w-4" />
                <strong>Warning: File has dependencies</strong>
              </div>
              <p className="text-sm text-destructive/80 mb-3">
                This file is referenced by {dependencies.length} other file
                {dependencies.length !== 1 ? 's' : ''}. Deleting it will break
                those references.
              </p>

              <div className="space-y-2">
                <div className="text-sm font-medium text-destructive">
                  Files that reference this file:
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {dependencies.map((dep) => (
                    <div
                      key={dep.id}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div className="w-2 h-2 bg-destructive/60 rounded-full" />
                      <span className="flex-1">{dep.path}</span>
                      <Badge variant="outline" className="text-xs">
                        {dep.metadata.type}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Safe deletion info */}
          {!hasDependencies && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="text-sm text-green-800">
                ✓ No other files reference this file. Safe to delete.
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-medium">Error:</span>
              </div>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          )}

          {/* Deletion consequences */}
          <div className="space-y-2">
            <Separator />
            <div className="text-sm text-muted-foreground">
              <strong>What will be deleted:</strong>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• File content and metadata</li>
              <li>• All revision history</li>
              <li>• Any @ references pointing to this file will be broken</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Deleting...
              </div>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                {hasDependencies ? 'Delete Anyway' : 'Delete'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
